// ============================================================
// 路由守卫工厂
// - 标题：${meta.title} - ${baseTitle}
// - 鉴权：requireAuth=true 且白名单外要求 token，否则跳 /login?redirect=...
// - 动态路由：首次进业务页时拉 menus → 单 Layout addRoute
// - 无路由 → /404；有路由无权限码 → /403；并过滤顶部页签
// - TagsView：afterEach 自动 addView（hidden / 403 / 404 除外）
// ============================================================
import type { RouteComponent, RouteLocationNormalized, Router } from "vue-router";
import type { UserMenuItem } from "@shared/types/user";
import { useAuthStore } from "@shared/store/modules/auth";
import { usePermissionStore } from "@shared/store/modules/permission";
import { resolveRouteAccessDenial } from "@shared/utils/tagNavigation";
import { fallbackRoute } from "./constantRoutes";
import { getRoutePageExtra } from "./routePageExtras";
import { setSingletonNavOverwritePending } from "./singletonNavPending";

export interface InstallGuardsOptions {
    whiteList: string[];
    baseTitle: string;
    layout: RouteComponent;
    fetchUserMenus?: () => Promise<UserMenuItem[]>;
    requireAuth?: boolean;
}

function resolveRouteIdentity(route: Pick<RouteLocationNormalized, "query" | "params">, key = "id"): string {
    const fromQuery = route.query[key];
    const fromParams = route.params[key];
    const raw = fromQuery ?? fromParams;
    const value = Array.isArray(raw) ? raw[0] : raw;
    return value != null && value !== "" ? String(value) : "";
}

function resolveTagIdentity(tag: { fullPath: string; query?: Record<string, unknown> }, key = "id"): string {
    const fromQuery = tag.query?.[key];
    if (fromQuery != null && fromQuery !== "") return String(fromQuery);
    const editMatch = tag.fullPath.match(/\/edit\/([^/?]+)/);
    if (editMatch?.[1]) return editMatch[1];
    try {
        const url = new URL(tag.fullPath, "http://local");
        const q = url.searchParams.get(key);
        if (q) return q;
    } catch {
        /* ignore */
    }
    return "";
}

function resolveTagPath(route: RouteLocationNormalized): string {
    return (route.meta?.singletonTagPath as string) || route.path;
}

export function installGuards(router: Router, opts: InstallGuardsOptions): void {
    const { whiteList, baseTitle, layout, fetchUserMenus, requireAuth = true } = opts;

    router.beforeEach(async (to, from, next) => {
        document.title = to.meta?.title ? `${to.meta.title} - ${baseTitle}` : baseTitle;

        const auth = useAuthStore();

        if (whiteList.includes(to.path)) return next();

        if (requireAuth && !auth.token) {
            return next(`/login?redirect=${encodeURIComponent(to.fullPath)}`);
        }

        if (to.meta?.singletonNavConfirm) {
            const identityKey = (to.meta.singletonIdentity as string) || "id";
            const toId = resolveRouteIdentity(to, identityKey);
            if (toId) {
                const { useTagsViewStore } = await import("@shared/store/modules/tagsView");
                const tagsStore = useTagsViewStore();
                const tagPath = resolveTagPath(to);
                const existing = tagsStore.visitedViews.find(v => v.path === tagPath);
                const openId = existing ? resolveTagIdentity(existing, identityKey) : "";

                if (existing && openId && openId !== toId) {
                    setSingletonNavOverwritePending({
                        fromFullPath: existing.fullPath,
                        fromId: openId,
                        toId,
                    });
                }
            }
        }

        if (!requireAuth) return next();

        if (!fetchUserMenus) {
            console.warn("[guards] 未提供 fetchUserMenus，跳过动态路由阶段");
            return next();
        }

        const permission = usePermissionStore();
        if (!permission.loaded) {
            try {
                const records = await permission.generateRoutes({ layout, fetchUserMenus });
                records.forEach(r => router.addRoute(r));
                ensureFallbackRouteLast(router);
                await syncTagsAfterRoutesReady(router);
                const denial = resolveRouteAccessDenial(to.path, router);
                if (denial) {
                    await dropDeniedTag(router, to.path);
                    return next({ path: denial === "forbidden" ? "/403" : "/404", replace: true });
                }
                // 不要展开旧的 to（matched/name 还是 addRoute 之前的），按 path 重新匹配
                return next({ path: to.path, query: to.query, hash: to.hash, replace: true });
            } catch (err) {
                console.error("[guards] generateRoutes failed:", err);
                await auth.logout();
                return next(`/login?redirect=${encodeURIComponent(to.fullPath)}`);
            }
        }

        const denial = resolveRouteAccessDenial(to.path, router);
        if (denial) {
            await dropDeniedTag(router, to.path);
            return next({ path: denial === "forbidden" ? "/403" : "/404", replace: true });
        }

        const extra = getRoutePageExtra(typeof to.name === "string" ? to.name : "");
        if (extra?.interceptClick && from.matched.length > 0) {
            extra.interceptClick();
            return next(false);
        }

        next();
    });

    router.afterEach(async to => {
        if (to.path === "/404" || to.path === "/403") return;
        if (to.meta?.hidden) return;
        if (!to.name) return;
        const { useTagsViewStore } = await import("@shared/store/modules/tagsView");
        useTagsViewStore().addView(to);
    });
}

async function syncTagsAfterRoutesReady(router: Router): Promise<void> {
    const { useTagsViewStore } = await import("@shared/store/modules/tagsView");
    useTagsViewStore().pruneInaccessibleViews(router);
}

async function dropDeniedTag(router: Router, path: string): Promise<void> {
    const { useTagsViewStore } = await import("@shared/store/modules/tagsView");
    const tagsStore = useTagsViewStore();
    tagsStore.dropPath(path);
    tagsStore.pruneInaccessibleViews(router);
}

function ensureFallbackRouteLast(router: Router): void {
    const name = fallbackRoute.name as string;
    if (router.hasRoute(name)) {
        router.removeRoute(name);
    }
    router.addRoute(fallbackRoute);
}

export function clearDynamicRoutes(router: Router): void {
    const permission = usePermissionStore();
    const names = permission.reset();
    for (const name of names) {
        if (router.hasRoute(name)) router.removeRoute(name);
    }
    const fallbackName = fallbackRoute.name as string;
    if (router.hasRoute(fallbackName)) {
        router.removeRoute(fallbackName);
    }
}

/**
 * 403 / 404 回首页：清会话缓存与动态路由，下一跳由守卫重新拉 user/info（菜单/权限/按钮页）。
 * 先落到 /dashboard，各端桥接页再按新权限进真正首页。
 */
export async function reloadSessionAndGoHome(router: Router): Promise<void> {
    const { useCurrentUserStore } = await import("@app/store/modules/currentUser");
    useCurrentUserStore().reset();
    clearDynamicRoutes(router);
    await router.replace("/dashboard");
}
